import {
  animate,
  keyframes,
  style,
  transition,
  trigger
} from '@angular/animations';
import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import { Strategy, StrategySummary } from '@approot/models';
import {
  AdminService,
  ComponentVisibilityService,
  ConfigService,
  StrategiesService
} from '@approot/services';
import { SubscriptionSink, Utilities } from '@approot/utilities';
import { Observable, forkJoin } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';

const textFlashAnimation = animate(
  3000,
  keyframes([
    style({
      opacity: 0,
      offset: 0
    }),
    style({
      opacity: 1,
      offset: 0.2
    }),
    style({
      opacity: 0,
      offset: 0.4
    }),
    style({
      opacity: 1,
      offset: 0.6
    }),
    style({
      opacity: 0,
      offset: 0.8
    }),
    style({
      opacity: 1,
      offset: 1
    })
  ])
);
@Component({
  selector: 'app-strategies-viewer',
  templateUrl: './strategies-viewer.component.html',
  styleUrls: ['./strategies-viewer.component.scss'],
  animations: [trigger('flashText', [transition('* => *', textFlashAnimation)])]
})
export class StrategiesViewerComponent implements OnInit, OnDestroy {
  @Output() loadStrategies = new EventEmitter();
  @Output() deleteStrategy = new EventEmitter<Strategy>();

  public receivedStrategies$: Observable<Strategy[]>;
  public sharedStrategies$: Observable<Strategy[]>;
  public savedStrategies$: Observable<Strategy[]>;

  public savedActive = true;
  public sentActive = false;
  public receivedActive = false;
  public receivedStrategyToFlash: Strategy;

  public savedColumns = [
    {
      dbName: 'name',
      displayName: 'Strategy Name'
    },
    {
      dbName: 'timestamp',
      displayName: 'Date Saved'
    }
  ];
  public receivedColumns = [
    {
      dbName: 'name',
      displayName: 'Strategy Name'
    },
    {
      dbName: 'shared',
      displayName: 'Shared by'
    },
    {
      dbName: 'timestamp',
      displayName: 'Date Received'
    },
    {
      dbName: 'message',
      displayName: 'Message'
    }
  ];
  public sentColumns = [
    {
      dbName: 'name',
      displayName: 'Strategy Name'
    },
    {
      dbName: 'shared',
      displayName: 'Shared with'
    },
    {
      dbName: 'timestamp',
      displayName: 'Date Sent'
    },
    {
      dbName: 'message',
      displayName: 'Message'
    }
  ];

  public selectedSaved: StrategySummary;
  public selectedSent: StrategySummary;
  public selectedReceived: StrategySummary;

  private subsink = new SubscriptionSink();

  constructor(
    private strategiesService: StrategiesService,
    private adminService: AdminService,
    private componentVisibilityService: ComponentVisibilityService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private configService: ConfigService
  ) {
    this.configService.getUserSettings().subscribe(async config => {
      if (!config.parent.disableStrategyMessages) {
        return;
      }
      this.sentColumns = this.sentColumns.filter(c => c.dbName !== 'message');
      this.receivedColumns = this.receivedColumns.filter(
        c => c.dbName !== 'message'
      );
    });
  }
  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  ngOnInit() {
    // the strategies observable contains all strategies the user can view,
    // the ones that were shared to them contain the shared property...
    this.subsink.sink = this.strategiesService.stratetgiesToFlash$.subscribe(
      x => {
        this.receivedStrategyToFlash = x;
      }
    );

    this.receivedStrategies$ = this.strategiesService.strategies.pipe(
      map((strats: Strategy[]) => {
        return strats.filter(s => s.shared);
      })
    );

    // ...the ones that don't have the shared property were created
    // and saved by the user
    this.savedStrategies$ = this.strategiesService.strategies.pipe(
      map((strats: Strategy[]) => {
        return strats.filter(s => !s.shared);
      })
    );

    // ...shared strategies were shared out by current user
    this.sharedStrategies$ = this.strategiesService.sharedStrategies;

    if (!this.strategiesService.isDataStoreInit) {
      // we only need to load the strategies once, the websocket will add new ones that come in.
      forkJoin({
        userStrategies: this.strategiesService.getStrategiesInfo(
          'getUserStrategiesSummary'
        ),
        userSentStrategies: this.strategiesService.getStrategiesInfo(
          'getUserSentStrategiesSummary'
        )
      }).subscribe({
        next: (response: {
          userStrategies: StrategySummary[];
          userSentStrategies: StrategySummary[];
        }) => {
          this.strategiesService.initDataStore();
          this.strategiesService.setUserSentStrategies(
            response.userSentStrategies
          );
          this.strategiesService.setUserStrategies(response.userStrategies);
        }
      });
    }
  }

  public wipeStrategyToFlash(): void {
    this.receivedStrategyToFlash = null;
  }

  public newStrategyReceived(strategy: Strategy) {
    this.savedActive = false;
    this.sentActive = false;
    this.receivedActive = true;
    this.subsink.sink = this.receivedStrategies$
      .pipe(
        skipWhile(x => !x.length),
        take(1)
      )
      .subscribe(_ => {
        setTimeout(() => {
          let e = this.elementRef.nativeElement.querySelector(
            '.strategies-container__data-grid__container--received .datagrid'
          );
          this.renderer.setProperty(e, 'scrollTop', e.scrollHeight);
          this.receivedStrategyToFlash = strategy;
        }, 0);
      });
  }

  updateStrategiesGridVis(val) {
    this.componentVisibilityService.updateStrategiesGridVis(val);
  }

  getUserEmail(userId: string): string {
    return this.adminService.getUserEmail(userId);
  }

  loadStrategyClick() {
    let selectedStrategy = this.getActiveTabSelectedItem();
    if (selectedStrategy) {
      this.strategiesService
        .getStrategies({
          task: 'getStrategy',
          table: `strategies-${Utilities.currentEnvironment()}`,
          userId: selectedStrategy.userId,
          timestamp: selectedStrategy.timestamp
        })
        .subscribe({
          next: (strategy: Strategy) => {
            this.loadStrategies.emit(strategy);
          }
        });
    }
  }

  deleteStrategyClick() {
    let selectedStrategy = this.getActiveTabSelectedItem();
    if (selectedStrategy) {
      this.deleteStrategy.emit(selectedStrategy);
    }
  }

  getActiveTabSelectedItem(): StrategySummary {
    if (this.savedActive) {
      return this.selectedSaved;
    } else if (this.sentActive) {
      return this.selectedSent;
    } else if (this.receivedActive) {
      return this.selectedReceived;
    } else return null;
  }
}
